import { useMutation, useQueryClient, UseMutationResult } from '@tanstack/react-query';
import { updateUserAgents as updateUserAgentsAPI } from 'src/common/Scraper';
import { UpdateUserAgentsPayload, UpdateUserAgentsResponse } from '../types/user-agents.type';
import { OrbitAIAgents } from '../types/types';
import { useDispatch } from 'react-redux';
import { setOrbitAISnackBar } from 'src/redux/siteAuditReducer';

import { posthogService, PostHogEvent } from 'src/common/services/posthogService';

type UseUpdateUserAgentsProps = OrbitAIAgents

/**
 * React Query mutation hook to update Orbit AI agent settings.
 * Automatically invalidates 'getUserAgentDetails' query cache on success
 * and records PostHog event tracking for agent lifecycle (enable/disable) with origin source attribution.
 */
export const useUpdateUserAgents = (agent?: UseUpdateUserAgentsProps): UseMutationResult<
  UpdateUserAgentsResponse,
  Error,
  UpdateUserAgentsPayload
> => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient();

  return useMutation<UpdateUserAgentsResponse, Error, UpdateUserAgentsPayload>({
    mutationKey: ['updateUserAgents', agent],
    mutationFn: (payload: UpdateUserAgentsPayload) => updateUserAgentsAPI(payload),
    onSuccess: (_, variables) => {
      if (variables?.site_id) {
        queryClient.invalidateQueries({
          queryKey: ['getUserAgentDetails', variables.site_id],
        });
      }

      // Track Orbit AI agent enable/disable toggle event in PostHog
      // Captures the agent name, boolean status, site ID, and origin source (e.g. dashboard card, agent dashboard, onboarding, reaudit)
      if (variables?.agents && variables.agents.length > 0) {
        variables.agents.forEach((agentItem) => {
          posthogService.track(PostHogEvent.ORBIT_AI_AGENT_TOGGLED, {
            agent_name: agentItem.name,
            enabled: agentItem.enabled,
            site_id: variables?.site_id,
            source: variables?.source || 'unknown',
            config_summary: agentItem.config ? Object.keys(agentItem.config) : []
          });
        });
      }
    },
    onError: (error) => {
      console.error('Error updating user agents:', error);
      // dispatch error for orbit AI
      dispatch(setOrbitAISnackBar({
        open: true,
        message: error?.message || 'Failed to update agents',
        type: 'error',
      }));
    },
  });
};

export default useUpdateUserAgents;
